<template>
  <!-- Figma 1:13536 › ລໍຖ້າໂອນເງິນ (711:2166), ໂອນສຳເລັດ (711:3669),
       ກວດສອບຍອດຄ້າງຊຳລະ (2168:4334). -->
  <div class="container list-page cod-transfers">
    <div class="cod-tabs">
      <button v-for="tab in visibleTabs" :key="tab.key" type="button"
              class="cod-tab" :class="{'is-active': activeTab === tab.key}"
              @click="changeTab(tab.key)">
        {{ tab.title }}
      </button>
    </div>

    <AuditTab v-if="activeTab === 'audit' && canManage"/>
    <DebtHistoryTab v-else-if="activeTab === 'debt' && canManage"/>

    <div v-else>
      <div class="list-head">
        <h2 class="list-title">
          <i class="fal" :class="heading.icon"></i>
          {{ heading.title }}
          <span class="list-count">({{ pagination.total }})</span>
        </h2>
        <p class="list-total">{{ heading.totalLabel }} {{ formatKip(heading.total) }}</p>
      </div>

      <div class="filter-card">
        <div class="filter-field">
          <label for="cod-transfers-search">ຄົ້ນຫາ:</label>
          <input id="cod-transfers-search" class="filter-input" type="text" v-model="filters.search"
                 placeholder="ຊື່ຮ້ານ / ເບີໂທ / ອິນວອຍ" @keyup.enter="applyFilters">
        </div>
        <div class="filter-field">
          <label for="cod-transfers-shop-status">ສະຖານະຮ້ານ:</label>
          <select id="cod-transfers-shop-status" class="filter-input" v-model="filters.shop_status"
                  @change="applyFilters">
            <option value="all">ທັງໝົດ</option>
            <option value="enabled">ເປີດນຳໃຊ້</option>
            <option value="disabled">ປິດນຳໃຊ້</option>
          </select>
        </div>
        <!-- Rejected requests have no tab of their own in the design; they are
             closed rows, so they are one choice away on the history tab. -->
        <div class="filter-field" v-if="activeTab === 'history'">
          <label for="cod-transfers-status">ສະຖານະລາຍການ:</label>
          <select id="cod-transfers-status" class="filter-input" v-model="filters.history_status"
                  @change="applyFilters">
            <option value="success">ສຳເລັດ</option>
            <option value="rejected">ປະຕິເສດ</option>
          </select>
        </div>
        <!-- Look back by date and export what was paid to sign it (Finance,
             2026-10-26): the transfer day of a paid row, the refusal day of a
             rejected one. Both empty = every date. -->
        <div class="filter-field" v-if="activeTab === 'history'">
          <label for="cod-transfers-from">ວັນທີ:</label>
          <div class="date-range">
            <input id="cod-transfers-from" class="filter-input" type="date" v-model="filters.from"
                   :max="filters.to || undefined" @change="applyDates">
            <span class="date-sep">-</span>
            <input id="cod-transfers-to" class="filter-input" type="date" v-model="filters.to" aria-label="ຫາວັນທີ"
                   :min="filters.from || undefined" @change="applyDates">
            <button type="button" class="cod-link" v-if="filters.from || filters.to" @click="clearDates">ທຸກວັນທີ</button>
          </div>
        </div>
        <button class="round-btn" type="button" aria-label="ຄົ້ນຫາ" @click="applyFilters">
          <i class="fal fa-search"></i>
        </button>
      </div>

      <!-- The legacy COD page's export buttons (2026-10-16), on the ticked rows,
           and since 2026-10-26 its ອັບເດດ N Invoice: Finance pays the ticked rows
           in ONE I-Bank transfer and records them paid together, no slip. Its
           ລາຍການລູກຄ້າບໍ່ກົດສະຫຼຸບຍອດ stays absent. -->
      <div class="export-card" v-if="canExport">
        <div class="export-select">
          <span>ເລືອກແລ້ວ <b>{{ selectedIds.length }}</b> ລາຍການ</span>
          <button type="button" class="cod-link" :disabled="selectingAll || !pagination.total" @click="selectAll">
            {{ selectingAll ? 'ກຳລັງເລືອກ...' : 'ເລືອກທັງໝົດ ' + pagination.total + ' ລາຍການ' }}
          </button>
          <button type="button" class="cod-link is-danger" v-if="selectedIds.length" @click="clearSelection">
            ລ້າງການເລືອກ
          </button>
          <span class="cell-note" v-if="selectionNote">{{ selectionNote }}</span>
        </div>
        <div class="export-buttons">
          <button type="button" class="cod-btn is-pale" :disabled="!selectedIds.length || exporting"
                  @click="exportFile('summary')">
            Export - {{ selectedIds.length }} ( ສະຫຼຸບລວມ )
          </button>
          <button type="button" class="cod-btn is-pale" :disabled="!selectedIds.length || exporting"
                  @click="exportFile('details')">
            Export - {{ selectedIds.length }} ( ລາຍລະອຽດລວມ )
          </button>
          <!-- The bank upload pays every row in it: waiting rows only, and it
               claims them for you (the server refuses rows held by others). -->
          <template v-if="activeTab === 'open'">
            <button type="button" class="cod-btn is-danger" :disabled="!selectedIds.length || exporting"
                    @click="askBankExport('BCEL')">
              Export - {{ selectedIds.length }} ( IBank-BCEL )
            </button>
            <button type="button" class="cod-btn" :disabled="!selectedIds.length || exporting"
                    @click="askBankExport('LDB')">
              Export - {{ selectedIds.length }} ( IBank-LDB )
            </button>
            <button type="button" class="cod-btn" :disabled="!selectedIds.length || exporting"
                    @click="openMarkPaid">
              ອັບເດດ {{ selectedIds.length }} Invoice
            </button>
          </template>
        </div>
        <p class="list-state is-error" v-if="exportError">{{ exportError }}</p>
      </div>

      <div class="table-card">
        <vs-table :data="rows">
          <template #thead>
            <vs-tr>
              <vs-th v-if="canExport">
                <input type="checkbox" class="row-check" aria-label="ເລືອກທັງໝົດໃນໜ້ານີ້"
                       :checked="pageAllSelected" :indeterminate.prop="pagePartlySelected"
                       @change="togglePage($event.target.checked)">
              </vs-th>
              <vs-th>Action</vs-th>
              <vs-th>ລຳດັບ</vs-th>
              <vs-th>ຊື່ຮ້ານ</vs-th>
              <vs-th>ເບີໂທ</vs-th>
              <vs-th>ອິນວອຍ</vs-th>
              <vs-th>ຈຳນວນເງິນ</vs-th>
              <vs-th>ປະເພດຍອດເງິນ COD</vs-th>
              <vs-th>% ໃນການຖອນ</vs-th>
              <vs-th>ຄ່າທຳນຽມ</vs-th>
              <vs-th>ສະຖານະ</vs-th>
              <vs-th>ວັນທີຂໍ</vs-th>
            </vs-tr>
          </template>
          <template #tbody>
            <vs-tr :key="row.id" v-for="(row, index) in rows">
              <vs-td v-if="canExport">
                <input type="checkbox" class="row-check" :aria-label="'ເລືອກ ' + (row.invoice_number || row.id)"
                       :checked="isSelected(row.id)" @change="toggleRow(row.id, $event.target.checked)">
              </vs-td>
              <vs-td>
                <!-- Waiting: one button, as designed. It claims the row (the
                     server refuses a slip for an unclaimed row) and opens the
                     slip form; pressing it again on your own claim resumes it. -->
                <div class="action-cell" v-if="activeTab === 'open'">
                  <button v-if="canTransfer(row)" class="cod-btn is-sm" :disabled="claimingId === row.id"
                          @click="startTransfer(row)">
                    ຢືນຢັນ ແລະ ແນບສະລິບໂອນ
                  </button>
                  <!-- Held by another operator: never offer an action that would
                       let a second person wire the same payout. -->
                  <span v-else class="pill is-claimed">
                    <i class="fal fa-user-lock"></i> {{ row.claimed_by.name }}
                  </span>
                  <!-- ↺ hand back / ✕ reject as icons, named on hover: a ⋯ said
                       nothing about what was behind it. -->
                  <button v-for="item in menuItems(row)" :key="item.key" type="button"
                          class="icon-btn" :class="{'is-danger': item.danger}"
                          :title="item.label" :aria-label="item.label" @click="onMenu(row, item.key)">
                    <i class="fal" :class="item.icon"></i>
                  </button>
                </div>
                <template v-else>
                  <button v-if="row.transfer_slip_url" type="button" class="thumb is-clickable"
                          title="ສະລິບໂອນ" aria-label="ເບິ່ງສະລິບໂອນ" @click="openSlip(row)">
                    <img :src="row.transfer_slip_url" alt="">
                  </button>
                  <!-- Paid by ອັບເດດ N Invoice: one bank transfer for many rows, no slip of its own. -->
                  <span v-else-if="row.status === 'success' && row.settled_via !== 'netted'" class="pill is-success"
                        title="ໂອນລວມຜ່ານ I-Bank (ບໍ່ມີສະລິບແຍກ)">
                    <i class="fal fa-layer-group"></i> ໂອນລວມ
                  </span>
                  <span v-else class="text-muted">—</span>
                </template>
              </vs-td>
              <vs-td>{{ rowNumber(index) }}</vs-td>
              <vs-td>{{ row.shop ? row.shop.name : '—' }}</vs-td>
              <vs-td>{{ row.customer ? row.customer.tel : '—' }}</vs-td>
              <!-- The number the app and the debt history name (owner, 2026-10-08). -->
              <vs-td>{{ row.invoice_number || '—' }}</vs-td>
              <vs-td>{{ formatKip(row.total_price) }}</vs-td>
              <vs-td>{{ typeLabel(row) }}</vs-td>
              <!-- The percent and its amount (owner, 2026-10-07): what was
                   drawn, and the fee taken off the transfer. -->
              <vs-td>
                {{ formatPct(row.advance_rate_pct) }}
                <span class="cell-note" v-if="advanceOf(row) > 0">ຖອນ {{ formatKip(advanceOf(row)) }}</span>
              </vs-td>
              <vs-td>
                {{ formatPct(row.advance_fee_pct) }}
                <span class="cell-note" v-if="feeOf(row) > 0">{{ formatKip(feeOf(row)) }}</span>
              </vs-td>
              <vs-td>
                <span :class="statusOf(row).tone">{{ statusOf(row).label }}</span>
                <span class="cell-note" v-if="statusOf(row).note">{{ statusOf(row).note }}</span>
              </vs-td>
              <vs-td>{{ formatDateTime(row.created_at) }}</vs-td>
            </vs-tr>
          </template>
          <template #footer>
            <Pagination v-if="pagination.last_page > 1" :pagination="pagination" :offset="10" @paginate="fetchData"/>
          </template>
        </vs-table>

        <p class="list-state is-loading" v-if="isLoading">ກຳລັງໂຫລດ...</p>
        <p class="list-state" v-else-if="!rows.length">ບໍ່ມີລາຍການ</p>
        <p class="list-state is-error" v-if="error">{{ error }}</p>
      </div>
    </div>

    <vs-dialog prevent-close v-model="slipActive" v-if="slipActive" width="520px">
      <template #header>
        <h4 class="dialog-title">ອັບໂຫລດສລິບການໂອນເງິນ</h4>
      </template>
      <SlipUploadModal :invoice="active" :bank="activeBank" @close="slipActive = false" @done="afterSlip"/>
    </vs-dialog>

    <!-- Figma 711:3179 — shown once the slip is accepted. -->
    <vs-dialog v-model="doneActive" v-if="doneActive" width="370px" not-close>
      <div class="done">
        <i class="fal fa-check-circle done-icon"></i>
        <b class="done-title">ສຳເລັດ</b>
        <div class="info-box">
          <div class="info-row">
            <span>ຮ້ານຄ້າ</span>
            <b>{{ done.shopName }}</b>
          </div>
          <div class="info-row">
            <span>ຈຳນວນເງິນ</span>
            <b>{{ formatKip(done.amount) }}</b>
          </div>
        </div>
        <button class="cod-btn is-block" type="button" @click="doneActive = false">ຕົກລົງ</button>
      </div>
    </vs-dialog>

    <!-- The slip opens here instead of in a new tab. -->
    <vs-dialog v-model="slipViewActive" v-if="slipViewActive" width="560px">
      <template #header>
        <h4 class="dialog-title">ສະລິບໂອນ</h4>
      </template>
      <div class="slip-view">
        <div class="info-box">
          <div class="info-row">
            <span>ຮ້ານຄ້າ</span>
            <b>{{ slipView.shopName }}</b>
          </div>
          <div class="info-row">
            <span>ເລກອິນວອຍ</span>
            <b>{{ slipView.invoiceNumber }}</b>
          </div>
          <div class="info-row">
            <span>ຈຳນວນເງິນ</span>
            <b>{{ formatKip(slipView.amount) }}</b>
          </div>
          <div class="info-row" v-if="slipView.paidAt">
            <span>ວັນທີໂອນ</span>
            <b>{{ formatDateTime(slipView.paidAt) }}</b>
          </div>
        </div>
        <img v-if="!slipView.failed" class="slip-image" :src="slipView.url" alt="ສະລິບໂອນ"
             @error="slipView.failed = true">
        <p v-else class="list-state is-error">ໂຫລດຮູບສະລິບບໍ່ໄດ້</p>
      </div>
    </vs-dialog>

    <vs-dialog v-model="bankConfirm.active" v-if="bankConfirm.active" width="460px">
      <template #header>
        <h4 class="dialog-title">Export IBank-{{ bankConfirm.bank }}</h4>
      </template>
      <div class="bank-confirm">
        <p>
          ລາຍການທີ່ຈ່າຍເຂົ້າທະນາຄານ {{ bankConfirm.bank }} ໃນ {{ selectedIds.length }} ລາຍການທີ່ເລືອກ
          ຈະຖືກ<b>ຮັບໄວ້ໃນຊື່ຂອງທ່ານ</b> ເພື່ອບໍ່ໃຫ້ຄົນອື່ນໂອນຊ້ຳ.
        </p>
        <p>ຫຼັງໂອນເງິນແລ້ວ ກົດ <b>ອັບເດດ Invoice</b> ເພື່ອບັນທຶກວ່າໂອນແລ້ວທັງໝົດໃນເທື່ອດຽວ
          (ຫຼື ແນບສະລິບໂອນເປັນລາຍການ — ຍອດໃນສະລິບຕ້ອງເທົ່າກັບຍອດຂອງລາຍການນັ້ນ).</p>
        <p class="list-state is-error" v-if="exportError">{{ exportError }}</p>
        <div class="dialog-actions">
          <button type="button" class="cod-btn is-pale" @click="bankConfirm.active = false">ຍົກເລີກ</button>
          <button type="button" class="cod-btn" :disabled="exporting" @click="exportBank">
            {{ exporting ? 'ກຳລັງ Export...' : 'ຢືນຢັນ Export' }}
          </button>
        </div>
      </div>
    </vs-dialog>

    <!-- ອັບເດດ N Invoice — the legacy COD page's modal: the bank's date and time + a note. -->
    <vs-dialog prevent-close v-model="markPaid.active" v-if="markPaid.active" width="460px">
      <template #header>
        <h4 class="dialog-title">ອັບເດດ {{ markPaid.ids.length }} Invoice ເປັນໂອນແລ້ວ</h4>
      </template>
      <div class="mark-paid">
        <template v-if="!markPaid.result">
          <p>ບັນທຶກວ່າ {{ markPaid.ids.length }} ລາຍການທີ່ເລືອກ ໂອນແລ້ວໃນການໂອນ I-Bank ຄັ້ງດຽວ. ບໍ່ຕ້ອງແນບສະລິບ.</p>
          <label class="label" for="cod-mark-paid-at">ວັນທີ ແລະ ເວລາໂອນ</label>
          <date-picker id="cod-mark-paid-at" v-model="markPaid.paidAt" type="datetime" format="DD/MM/YYYY HH:mm"
                       value-type="YYYY-MM-DD HH:mm:ss" :clearable="false" :disabled-date="afterToday"/>
          <label class="label" for="cod-mark-paid-remark">ໝາຍເຫດ</label>
          <input id="cod-mark-paid-remark" class="filter-input is-block" v-model="markPaid.remark" maxlength="255"
                 placeholder="ເລກອ້າງອີງ I-Bank / ໝາຍເຫດ...">
          <p class="list-state is-error" v-if="markPaid.error">{{ markPaid.error }}</p>
          <div class="dialog-actions">
            <button type="button" class="cod-btn is-pale" :disabled="markPaid.saving" @click="markPaid.active = false">ຍົກເລີກ</button>
            <button type="button" class="cod-btn" :disabled="markPaid.saving || !markPaid.paidAt" @click="submitMarkPaid">
              {{ markPaid.saving ? 'ກຳລັງບັນທຶກ...' : 'ຢືນຢັນໂອນແລ້ວ' }}
            </button>
          </div>
        </template>
        <template v-else>
          <p><i class="fal fa-check-circle text-ok"></i> ອັບເດດແລ້ວ <b>{{ markPaid.result.updated.length }}</b> ລາຍການ</p>
          <p v-if="markPaid.result.skipped.length">ໂອນແລ້ວກ່ອນໜ້ານີ້ (ຂ້າມ): {{ markPaid.result.skipped.length }} ລາຍການ</p>
          <div v-if="markPaid.result.failed.length" class="list-state is-error">
            ບໍ່ສຳເລັດ {{ markPaid.result.failed.length }} ລາຍການ (ລາຍການປ່ຽນໄປແລ້ວ ກະລຸນາກວດຄືນ):
            {{ markPaid.result.failed.map(f => f.invoice_number).join(', ') }}
          </div>
          <div class="dialog-actions">
            <button type="button" class="cod-btn" @click="markPaid.active = false">ຕົກລົງ</button>
          </div>
        </template>
      </div>
    </vs-dialog>

    <vs-dialog prevent-close v-model="reasonActive" v-if="reasonActive" width="460px">
      <template #header>
        <h4 class="dialog-title">{{ reasonTitle }}</h4>
      </template>
      <ReasonModal :note="reasonNote" :confirm-label="reasonConfirm"
                   :saving="reasonSaving" :error="reasonError"
                   @close="reasonActive = false" @submit="submitReason"/>
    </vs-dialog>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import Pagination from '@/Plugins/Paginations/Pagination';
import AuditTab from '@/views/Accounting/Customer/CodAdvance/components/AuditTab';
import DebtHistoryTab from '@/views/Accounting/Customer/CodAdvance/components/DebtHistoryTab';
import SlipUploadModal from '@/views/Accounting/Customer/CodAdvance/components/SlipUploadModal';
import ReasonModal from '@/views/Accounting/Customer/CodAdvance/components/ReasonModal';
import {QUEUE_REFRESH_EVENT} from '@/views/Accounting/Customer/CodAdvance/components/CodAdvanceAlertBell';
import {
  blobErrorMessage,
  claimTransfer,
  downloadTransferExport,
  errorMessage,
  fetchTransferIds,
  fetchTransfers,
  formatDateTime,
  formatKip,
  formatPct,
  markTransfersPaid,
  rejectTransfer,
  releaseTransfer,
} from '@/services/codAdvanceService';

export default {
  name: 'CodAdvanceTransfers',
  components: {DatePicker, Pagination, AuditTab, DebtHistoryTab, SlipUploadModal, ReasonModal},
  data() {
    return {
      activeTab: 'open',
      tabs: [
        {key: 'open', title: 'ລໍຖ້າໂອນເງິນ'},
        {key: 'history', title: 'ໂອນສຳເລັດ'},
        {key: 'audit', title: 'ກວດສອບຍອດຄ້າງຊຳລະ'},
        {key: 'debt', title: 'ປະຫວັດການຫັກໜີ້'},
      ],
      rows: [],
      sumTotal: 0,
      sumTransferred: 0,
      // All shops by default: a disabled shop's pending payout is still owed.
      filters: {search: '', shop_status: 'all', history_status: 'success', from: '', to: ''},
      // The range the listed rows were read with: the boxes may hold one end
      // while the other is still being picked, and ເລືອກທັງໝົດ must select
      // exactly what is listed.
      appliedRange: {from: '', to: ''},
      pagination: {current_page: 1, last_page: 1, per_page: 10, total: 0},
      error: null,
      isLoading: false,
      claimingId: null,
      active: null,
      activeBank: {},
      slipActive: false,
      doneActive: false,
      done: {shopName: '', amount: 0},
      slipViewActive: false,
      slipView: {url: '', shopName: '', invoiceNumber: '', amount: 0, paidAt: null, failed: false},
      reasonActive: false,
      reasonMode: null,
      reasonSaving: false,
      reasonError: null,
      // Export selection: ids, kept across pages, cleared on a tab or filter change.
      selectedIds: [],
      // Bumped whenever the selection changes by other means (tab, filters,
      // clear, a ticked box): a ເລືອກທັງໝົດ answer that arrives after it is dropped.
      selectionGeneration: 0,
      selectingAll: false,
      selectionNote: '',
      exporting: false,
      exportError: null,
      bankConfirm: {active: false, bank: ''},
      markPaid: {active: false, ids: [], paidAt: '', remark: '', saving: false, error: null, result: null, token: 0},
      markPaidSeq: 0,
    };
  },
  computed: {
    status() {
      return this.activeTab === 'open' ? 'open' : this.filters.history_status;
    },
    heading() {
      if (this.status === 'open') {
        return {
          icon: 'fa-clock',
          title: 'ລາຍການລໍຖ້າໂອນເງິນCOD',
          totalLabel: 'ຍອດທີ່ລໍຖ້າໂອນເງິນທັງໝົດ :',
          total: this.sumTotal,
        };
      }
      if (this.status === 'rejected') {
        return {icon: 'fa-ban', title: 'ລາຍການປະຕິເສດ', totalLabel: 'ຍອດປະຕິເສດ', total: this.sumTotal};
      }
      // Netted settlements are history rows too, but no money moved for them.
      return {icon: 'fa-check-circle', title: 'ລາຍການໂອນສຳເລັດ', totalLabel: 'ໂອນສຳເລັດ', total: this.sumTransferred};
    },
    reasonTitle() {
      return this.reasonMode === 'reject' ? 'ປະຕິເສດລາຍການຖອນ' : 'ຄືນລາຍການໃຫ້ຄິວ';
    },
    reasonNote() {
      if (this.reasonMode === 'reject') {
        return 'ການຈອງຈະຖືກປົດ, ໜີ້ທີ່ຫັກໄປຈະຖືກຄືນ ແລະ ພັດສະດຸຈະຖອນໄດ້ອີກຄັ້ງ';
      }
      // A SuperAdmin taking back someone else's claim: the claim exists so
      // one payout is wired once — if the claimer already sent the money and
      // only the slip is missing, releasing lets a second person pay it again.
      if (this.active && this.active.claimed_by && !this.isMine(this.active)) {
        const name = this.active.claimed_by.name;
        return 'ລາຍການທີ່ ' + name + ' ຮັບໄວ້ ຈະກັບໄປຢູ່ຄິວໃຫ້ຄົນອື່ນຮັບຕໍ່. '
            + 'ກະລຸນາກວດສອບກັບ ' + name + ' ກ່ອນວ່າຍັງບໍ່ໄດ້ໂອນເງິນ';
      }
      return 'ລາຍການຈະກັບໄປຢູ່ຄິວໃຫ້ຄົນອື່ນຮັບຕໍ່';
    },
    reasonConfirm() {
      return this.reasonMode === 'reject' ? 'ຢືນຢັນປະຕິເສດ' : 'ຢືນຢັນຄືນລາຍການ';
    },
    // The admin sign-in's `authUser` names the users.id `userId` and has no
    // `id` at all (ManageUserController::authUser). Reading `.id` made every
    // claim look like someone else's, so the claimer lost their own row:
    // no resume, no release, no reject.
    currentUserId() {
      const idOf = (user) => (user && (user.userId || user.id)) || null;
      const fromStore = idOf(this.$store.state.User.userProfile);
      if (fromStore) return fromStore;
      try {
        return idOf(JSON.parse(localStorage.getItem('user_profile') || '{}'));
      } catch (_) {
        return null;
      }
    },
    // Claims never expire: a SuperAdmin is the only way out of a claim whose
    // owner is absent (the API refuses anyone else's release).
    // The audit and debt-history endpoints sit behind role SuperAdmin|accountingcod
    // AND ability SuperAdmin|manage-cod-advance; the queue tabs also admit the
    // call centre and accountingmanager. A tab the API would answer 403 is not shown at all.
    canManage() {
      return this.$can('SuperAdmin') || this.$can(['accountingcod', 'manage-cod-advance'], true);
    },
    visibleTabs() {
      return this.tabs.filter(tab => this.canManage || (tab.key !== 'audit' && tab.key !== 'debt'));
    },
    isSuperAdmin() {
      return this.$can('SuperAdmin');
    },
    // Rejected rows moved no money: no payment document for them.
    canExport() {
      return this.status === 'open' || this.status === 'success';
    },
    pagePartlySelected() {
      const picked = this.rows.filter(row => this.isSelected(row.id)).length;
      return picked > 0 && picked < this.rows.length;
    },
    pageAllSelected() {
      return this.rows.length > 0 && this.rows.every(row => this.isSelected(row.id));
    },
  },
  created() {
    this.fetchData(1);
    window.addEventListener(QUEUE_REFRESH_EVENT, this.showWaiting);
  },
  beforeDestroy() {
    window.removeEventListener(QUEUE_REFRESH_EVENT, this.showWaiting);
  },
  methods: {
    formatDateTime,
    formatKip,
    formatPct,
    changeTab(key) {
      this.activeTab = key;
      this.clearSelection();
      // The audit and debt-history tabs load their own data.
      if (key !== 'audit' && key !== 'debt') {
        this.rows = [];
        this.fetchData(1);
      }
    },
    applyFilters() {
      this.clearSelection();
      this.fetchData(1);
    },
    // The bell was clicked while this page is open: show the waiting requests, fresh.
    showWaiting() {
      if (this.activeTab !== 'open') {
        this.changeTab('open');
      } else {
        this.fetchData(1);
      }
    },
    // The server filters by a range only with BOTH ends; one end alone waits for the other.
    applyDates() {
      if ((this.filters.from && this.filters.to) || (!this.filters.from && !this.filters.to)) {
        this.appliedRange = {from: this.filters.from, to: this.filters.to};
        this.applyFilters();
      }
    },
    clearDates() {
      this.filters.from = '';
      this.filters.to = '';
      this.appliedRange = {from: '', to: ''};
      this.applyFilters();
    },
    // The list's filters, shared by the page and ເລືອກທັງໝົດ (so an export holds what is listed).
    listParams() {
      const params = {
        status: this.status,
        search: this.filters.search || undefined,
        shop_status: this.filters.shop_status,
      };
      if (this.status !== 'open' && this.appliedRange.from && this.appliedRange.to) {
        params.from = this.appliedRange.from;
        params.to = this.appliedRange.to;
      }
      return params;
    },
    isSelected(id) {
      return this.selectedIds.includes(id);
    },
    // A hand-picked change wins over a ເລືອກທັງໝົດ still on its way.
    cancelSelectAll() {
      this.selectionGeneration += 1;
      this.selectingAll = false;
    },
    toggleRow(id, checked) {
      this.cancelSelectAll();
      this.selectedIds = checked
          ? (this.isSelected(id) ? this.selectedIds : [...this.selectedIds, id])
          : this.selectedIds.filter(picked => picked !== id);
    },
    togglePage(checked) {
      this.cancelSelectAll();
      const pageIds = this.rows.map(row => row.id);
      this.selectedIds = checked
          ? [...this.selectedIds, ...pageIds.filter(id => !this.isSelected(id))]
          : this.selectedIds.filter(id => !pageIds.includes(id));
    },
    clearSelection() {
      this.cancelSelectAll();
      this.selectedIds = [];
      this.selectionNote = '';
      this.exportError = null;
    },
    // Every row of the current filters, as far as one export takes.
    async selectAll() {
      if (this.selectingAll) return;
      const generation = this.selectionGeneration;
      this.selectingAll = true;
      this.exportError = null;
      try {
        const data = await fetchTransferIds(this.listParams());
        if (generation !== this.selectionGeneration) return;
        this.selectedIds = data.ids || [];
        this.selectionNote = data.truncated
            ? 'ເລືອກໄດ້ສູງສຸດ ' + this.selectedIds.length + ' ຈາກ ' + data.total + ' ລາຍການຕໍ່ການ Export ໜຶ່ງຄັ້ງ'
            : '';
      } catch (e) {
        if (generation === this.selectionGeneration) {
          this.exportError = errorMessage(e, 'ເລືອກລາຍການບໍ່ສຳເລັດ');
        }
      } finally {
        if (generation === this.selectionGeneration) {
          this.selectingAll = false;
        }
      }
    },
    exportFileName(kind, bank) {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const day = pad(now.getDate()) + '-' + pad(now.getMonth() + 1) + '-' + now.getFullYear();
      const name = {
        summary: 'COD ລ່ວງໜ້າ ສັງລວມ',
        details: 'COD ລ່ວງໜ້າ ລາຍລະອຽດລວມ',
        bank: 'COD ລ່ວງໜ້າ Transaction ' + bank,
      }[kind];
      return name + ' ວັນທີ ' + day + '.xlsx';
    },
    async exportFile(kind, bank = null) {
      if (this.exporting || !this.selectedIds.length) return false;
      this.exporting = true;
      this.exportError = null;
      try {
        const payload = {invoices: this.selectedIds};
        if (bank) payload.bank_name = bank;
        const response = await downloadTransferExport(kind, payload);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.exportFileName(kind, bank));
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return true;
      } catch (e) {
        this.exportError = await blobErrorMessage(e, 'Export ບໍ່ສຳເລັດ');
        return false;
      } finally {
        this.exporting = false;
      }
    },
    askBankExport(bank) {
      this.exportError = null;
      this.bankConfirm = {active: true, bank};
    },
    // The file's rows are now claimed by you: refresh so the list shows it.
    async exportBank() {
      const done = await this.exportFile('bank', this.bankConfirm.bank);
      if (done) {
        this.bankConfirm = {active: false, bank: ''};
        this.clearSelection();
      }
      this.fetchData(this.pagination.current_page);
    },
    afterToday(date) {
      return date > new Date();
    },
    nowStamp() {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      return now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate())
          + ' ' + pad(now.getHours()) + ':' + pad(now.getMinutes()) + ':00';
    },
    openMarkPaid() {
      if (this.markPaid.saving) return;
      this.markPaidSeq += 1;
      this.markPaid = {
        active: true, ids: [...this.selectedIds], paidAt: this.nowStamp(), remark: '', saving: false, error: null, result: null,
        token: this.markPaidSeq,
      };
    },
    // One request for the whole selection. A refused batch writes nothing; the
    // answer names what was updated, skipped (already paid) and failed.
    async submitMarkPaid() {
      if (this.markPaid.saving) return;
      // The answer belongs to THIS dialog only: one opened later never shows it.
      const state = this.markPaid;
      state.saving = true;
      state.error = null;
      try {
        const result = await markTransfersPaid({
          invoices: state.ids,
          paid_at: state.paidAt,
          remark: state.remark || undefined,
        });
        state.result = {
          updated: result.updated || [], skipped: result.skipped || [], failed: result.failed || [],
        };
        if (this.markPaid.token === state.token) {
          this.clearSelection();
        }
        this.fetchData(this.pagination.current_page);
      } catch (e) {
        state.error = errorMessage(e, 'ອັບເດດບໍ່ສຳເລັດ');
      } finally {
        state.saving = false;
      }
    },
    rowNumber(index) {
      return (this.pagination.current_page - 1) * this.pagination.per_page + index + 1;
    },
    // The advance drawn and its fee, from the row's breakdown (0 on a row without one).
    advanceOf(row) {
      return Number(row.breakdown && row.breakdown.advance_amount) || 0;
    },
    feeOf(row) {
      return Number(row.breakdown && row.breakdown.advance_fee) || 0;
    },
    isMine(row) {
      return !!row.claimed_by && !!this.currentUserId && String(row.claimed_by.id) === String(this.currentUserId);
    },
    canTransfer(row) {
      return !row.claimed_by || this.isMine(row);
    },
    menuItems(row) {
      const release = {key: 'release', label: 'ຄືນລາຍການໃຫ້ຄິວ', icon: 'fa-undo'};
      if (!this.canTransfer(row)) {
        return this.isSuperAdmin ? [release] : [];
      }
      const items = [];
      if (this.isMine(row)) {
        items.push(release);
      }
      items.push({key: 'reject', label: 'ປະຕິເສດ', icon: 'fa-times', danger: true});
      return items;
    },
    onMenu(row, key) {
      this.active = row;
      this.reasonMode = key;
      this.reasonError = null;
      this.reasonActive = true;
    },
    typeLabel(row) {
      // Since 2026-09-30 a plain COD-success request goes to the legacy COD page; a
      // `delivered` row lands here only when it also repays advance debt. Older plain
      // rows and combined rows (no longer created) keep their own labels.
      if (row.request_type === 'delivered' && Number(row.debt_recovered) > 0) {
        return 'ຈັດສົ່ງສຳເລັດ (ຫັກໜີ້ລ່ວງໜ້າ)';
      }
      return {
        delivered: 'ຈັດສົ່ງສຳເລັດ',
        delivered_and_advance: 'ຈັດສົ່ງສຳເລັດ + ຖອນລ່ວງໜ້າ',
        advance_only: 'ຖອນລ່ວງໜ້າ',
        auto_recovery: 'ຫັກໜີ້ອັດຕະໂນມັດ',
      }[row.request_type] || row.request_type;
    },
    statusOf(row) {
      if (row.status === 'claimed') {
        return {tone: 'text-busy', label: 'ກຳລັງໂອນ', note: row.claimed_by ? 'ໂດຍ ' + row.claimed_by.name : ''};
      }
      if (row.status === 'success') {
        return row.settled_via === 'netted'
            ? {tone: 'text-ok', label: 'ຊຳລະໜີ້', note: 'ບໍ່ມີການໂອນ'}
            : {tone: 'text-ok', label: 'ສຳເລັດ', note: ''};
      }
      if (row.status === 'rejected') {
        return {tone: 'text-bad', label: 'ປະຕິເສດ', note: row.reject_reason || ''};
      }
      return {tone: 'text-ok', label: 'ລໍຖ້າເງິນໂອນ', note: ''};
    },
    async fetchData(page) {
      this.error = null;
      this.isLoading = true;
      try {
        const data = await fetchTransfers({...this.listParams(), page: page || this.pagination.current_page});
        this.rows = data.data || [];
        this.sumTotal = data.sum_total_price || 0;
        this.sumTransferred = data.sum_transferred || 0;
        this.pagination = {
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
        };
      } catch (e) {
        this.rows = [];
        this.error = errorMessage(e, 'ໂຫລດຂໍ້ມູນບໍ່ສຳເລັດ');
      } finally {
        this.isLoading = false;
      }
    },
    // The bank account comes back only with the claim, and only to the operator
    // who holds it — so the claim always runs first, even to resume your own.
    async startTransfer(row) {
      if (this.claimingId) return;
      this.error = null;
      this.claimingId = row.id;
      try {
        const claimed = await claimTransfer(row.id);
        this.active = row;
        this.activeBank = claimed.bank || {};
        this.slipActive = true;
        this.fetchData(this.pagination.current_page);
      } catch (e) {
        this.error = errorMessage(e, 'ຮັບລາຍການບໍ່ສຳເລັດ');
        this.fetchData(this.pagination.current_page);
      } finally {
        this.claimingId = null;
      }
    },
    openSlip(row) {
      this.slipView = {
        url: row.transfer_slip_url,
        shopName: row.shop ? row.shop.name : '—',
        invoiceNumber: row.invoice_number || '—',
        amount: row.total_price,
        paidAt: row.paid_at,
        failed: false,
      };
      this.slipViewActive = true;
    },
    afterSlip(result) {
      const paid = result || {};
      this.done = {
        shopName: paid.shop ? paid.shop.name : (this.active.shop ? this.active.shop.name : ''),
        amount: paid.total_price !== undefined ? paid.total_price : this.active.total_price,
      };
      this.slipActive = false;
      this.doneActive = true;
      this.fetchData(this.pagination.current_page);
    },
    async submitReason(reason) {
      if (this.reasonSaving) return;
      this.reasonSaving = true;
      this.reasonError = null;
      try {
        if (this.reasonMode === 'reject') {
          await rejectTransfer(this.active.id, reason);
        } else {
          await releaseTransfer(this.active.id, reason);
        }
        this.reasonActive = false;
        this.fetchData(this.pagination.current_page);
      } catch (e) {
        this.reasonError = errorMessage(e, 'ດຳເນີນການບໍ່ສຳເລັດ');
      } finally {
        this.reasonSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './components/cod-advance-ui';

// Pulled up into the container's top padding so the tabs sit where the design
// puts them; any further and the global top bar covers them.
.cod-tabs {
  display: flex;
  gap: 8px;
  margin: -30px 0 18px;
  flex-wrap: wrap;
  border-bottom: 1px solid $cod-line;
}

.cod-tab {
  border: 0;
  background: none;
  padding: 10px 24px;
  margin-bottom: -1px;
  font-size: 15px;
  color: #4a5162;
  cursor: pointer;
  border-bottom: 2px solid transparent;

  &:hover {
    color: $cod-ink;
  }

  &.is-active {
    color: $cod-blue;
    font-weight: 600;
    border-bottom-color: $cod-blue;
  }
}

.export-card {
  background: #fff;
  border: 1px solid $cod-line;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 14px;
}

.export-select {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 14px;
  margin-bottom: 10px;
}

.export-buttons {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
}

.row-check {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.bank-confirm p {
  margin: 0 0 10px;
  line-height: 1.6;
}

.date-range {
  display: flex;
  align-items: center;
  gap: 6px;
}

.date-sep {
  margin: 0 2px;
}

.mark-paid {
  p {
    margin: 0 0 10px;
    line-height: 1.6;
  }

  .label {
    display: block;
    margin: 10px 0 4px;
    font-weight: 600;
  }

  .mx-datepicker {
    width: 100%;
  }

  .is-block {
    width: 100%;
  }
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}

.action-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.dialog-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  text-align: center;
  width: 100%;
}

.done {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: left;
  padding-top: 4px;

  .info-box {
    align-self: stretch;
    margin: 14px 0 16px;
  }
}

.done-icon {
  font-size: 58px;
  color: #1e8e4e;
}

// The slip tile is a button now: no button chrome, and a zoom cursor.
.thumb.is-clickable {
  padding: 0;
  cursor: zoom-in;
}

.slip-view .info-box {
  margin: 0 0 14px;
}

// Whatever height is left under the dialog header and the details, so the whole
// dialog stays on screen.
.slip-image {
  display: block;
  max-width: 100%;
  max-height: calc(100vh - 340px);
  min-height: 200px;
  margin: 0 auto;
  object-fit: contain;
  border-radius: 6px;
  border: 1px solid $cod-line;
}

.done-title {
  margin-top: 8px;
  font-size: 15px;
}
</style>
